import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  QaiFieldComponent,
  QaiInputComponent,
  QaiTextareaComponent,
  TextareaAutosizeDirective,
  QaiPasteCleanDirective,
  QaiFloatingSelectComponent,
} from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { ISimulationUpdate } from 'src/app/interfaces/self-serve/requests/isimulation-update';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { merge } from 'rxjs';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/author.service';
import { IModule } from 'src/app/interfaces/self-serve/responses/imodule';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';

@Component({
  selector: 'simulation-information',
  standalone: true,
  templateUrl: './simulation-information.component.html',
  styleUrls: ['./simulation-information.component.scss'],
  imports: [
    QaiFieldComponent,
    QaiInputComponent,
    FormsModule,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    DetailsSectionComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective,
    QaiFloatingSelectComponent,
  ],
})
export class SimulationInformationComponent {
  form!: FormGroup;
  callUuid: string = '';
  isSimulationNameAlreadyExists: boolean = false;
  moduleOptions: { label: string; value: string }[] = [];
  isLoadingModuleOptions: boolean = false;
  previousModuleUuid: string = '';
  readOnly: boolean = false;

  readonly languageOptions: { label: string; value: string }[] = [
    { label: 'English', value: 'en' },
    { label: 'French (France)', value: 'fr-FR' },
    { label: 'French (Canada)', value: 'fr-CA' },
    { label: 'Spanish (Spain)', value: 'es' },
    { label: 'Spanish (Mexico)', value: 'es-MX' },
    { label: 'Italian', value: 'it' },
    { label: 'German', value: 'de' },
    { label: 'Portuguese (Portugal)', value: 'pt-PT' },
    { label: 'Portuguese (Brazil)', value: 'pt-BR' },
  ];
  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private router: Router,
    private authorReactiveFormService: AuthorReactiveFormService
  ) {}

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);

    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => this.initFormControls(form));
  }

  private initFormControls(form: FormGroup): void {
    this.form = form.get(SimulationConstants.GENERAL_SECTION) as FormGroup;
    this.loadProducts();

    const nameControl = this.form.get('simulationName');
    const descriptionControl = this.form.get('simulationDescription');
    const moduleUuidControl = this.form.get('moduleUuid');
    const languageControl = this.form.get('language');

    this.previousModuleUuid = moduleUuidControl?.value || '';

    if (!nameControl || !descriptionControl || !languageControl) return;

    nameControl.valueChanges.subscribe(() => {
      this.isSimulationNameAlreadyExists = false;
    });

    if (moduleUuidControl) {
      this.listenToModuleChanges(moduleUuidControl);
    }

    this.listenToGeneralChanges(nameControl, descriptionControl, languageControl);
  }

  private listenToModuleChanges(moduleUuidControl: AbstractControl): void {
    moduleUuidControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged()
      )
      .subscribe((newModuleUuid: string) => {
        const updatedGeneralValue = this.form.value;
        this.authorReactiveFormService.updateFormUpdatedAt(this.callUuid);

        const currentModuleUuid = newModuleUuid || '';
        const existingModuleUuid = this.previousModuleUuid || '';

        const payload: ISimulationUpdate = {
          uuid: this.callUuid,
          name: (updatedGeneralValue.simulationName || '').trim(),
          description: updatedGeneralValue.simulationDescription,
          moduleUuid: currentModuleUuid,
          existingModuleUuid: existingModuleUuid,
          language: updatedGeneralValue.language,
        };

        this.previousModuleUuid = currentModuleUuid;
        this.sendRequestToUpdateSimulation(payload);
      });
  }

  private listenToGeneralChanges(
    nameControl: AbstractControl,
    descriptionControl: AbstractControl,
    languageControl: AbstractControl
  ): void {
    merge(
      nameControl.valueChanges,
      descriptionControl.valueChanges,
      languageControl.valueChanges
    )
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged()
      )
      .subscribe(() => {
        const updatedGeneralValue = this.form.value;
        this.authorReactiveFormService.updateFormUpdatedAt(this.callUuid);

        const payload: ISimulationUpdate = {
          uuid: this.callUuid,
          name: (updatedGeneralValue.simulationName || '').trim(),
          description: updatedGeneralValue.simulationDescription,
          moduleUuid: updatedGeneralValue.moduleUuid || '',
          existingModuleUuid: this.previousModuleUuid,
          language: updatedGeneralValue.language,
        };

        this.sendRequestToUpdateSimulation(payload);
      });
  }

  loadProducts(): void {
    const accountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();

    if (!accountUuid) {
      console.error('No account UUID found for loading module options');
      this.moduleOptions = [];
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.getModuleList(accountUuid).subscribe({
      next: (modules: IModule[]) => {
        this.moduleOptions = modules.map((module) => ({
          label: module.name,
          value: module.uuid,
        }));
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to load module options:', err);
        this.moduleOptions = [];
        this.isLoadingModuleOptions = false;
      },
    });
  }

  sendRequestToUpdateSimulation(payload: ISimulationUpdate): void {
    this.authorService.updateSimulation(payload).subscribe({
      next: (res: ISimulation) => {
        console.log(res);
        this.isSimulationNameAlreadyExists = false;

        const currentQueryParams = { ...this.route.snapshot.queryParams };
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            ...currentQueryParams,
            moduleUuid: payload?.moduleUuid || '',
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      },
      error: (err) => {
        console.error(err);
        if (err?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
          this.isSimulationNameAlreadyExists = true;
        }
      },
    });
  }

  onAddNewProduct(moduleName: string): void {
    if (!moduleName || moduleName.trim() === '') {
      return;
    }

    this.isLoadingModuleOptions = true;
    this.authorService.createModule(moduleName.trim()).subscribe({
      next: (newModule: IModule) => {
        this.moduleOptions.push({
          label: newModule.name,
          value: newModule.uuid,
        });
        const moduleUuidControl = this.form.get('moduleUuid');
        if (moduleUuidControl) {
          this.previousModuleUuid = moduleUuidControl.value || '';
          moduleUuidControl.setValue(newModule.uuid);
        }
        this.isLoadingModuleOptions = false;
      },
      error: (err: any) => {
        console.error('Failed to create new module:', err);
        this.isLoadingModuleOptions = false;
      },
    });
  }
}
